import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../redux/reducers";
import { fetchFcFacilities, fetchGeneralsForList, getG001ForHeadquarterUser, getG001ForStaff } from "../../services/api.service";
import {
  FCFacilitiesSchema,
  G001Schema,
  GeneralForListSchema,
} from "../../generated";
import { authSelector, userInfoSelector } from "../../redux/selectors/auth.selector";
import "./G001.scss";
import G002 from "../G002/G002";
import { HEADQUARTER_USER, PARENT, STAFF } from "../../constants";
import { initialGeneralForList } from "../../models/general.model";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import Pict, { SIZES } from "../../components/Pict/User";
import TextBox from "../../components/TextBox/TextBox";
import Select from "../../components/Select/CustomSelect";
import Split from "react-split"
import { useAuth } from "../../helper/auth.helper";
import { useLocation } from "react-router-dom";
import { getState } from "../../helper/page.helper";
import { notifyError } from "../../helper/settings.helper";
import { fcListSelector } from "../../redux/selectors/list.selector";
import { listActions } from "../../redux/actions/list.actions";
import { FC_LIST_TYPE, getFcList, getFcListForRedux, getFcListFromRedux } from "../../helper/list.helper";

interface iG001 {
  chat_user_id: number,
  chat_user_type: string,
}

const G001 = () => {
  const { checkLoggedIn } = useAuth();
  const authState = useSelector(authSelector);
  const userInfo = useSelector(userInfoSelector);
  const location = useLocation<iG001>();
  const state = getState(location);
  const [data, setData] = useState<G001Schema[]>();
  const wsState = useSelector((s: State) => s.wsReducer);
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [userType, setUserType] = useState<string>("");
  const [fcForSelect, setFcForSelect] = useState<GeneralForListSchema[]>([]);
  const [search, setSearch] = useState({
    facilityId: 0,
    rank: 2, // マネージャーのみ取得
    user_type_partner: "",
    name: "",
  });
  const [read, setRead] = useState(false)
  const fcListState = useSelector(fcListSelector);
  const dispatch = useDispatch();
  const fetchChatUsersRef = useRef<() => void>();

  const getSelectValue = (val: number, selectList: GeneralForListSchema[]) => {
    if (!val) return null;
    return selectList.filter((x) => x.value == val)[0];
  };

  const pushG002 = (id: number, type: string) => {
    setUserId(id);
    setUserType(type);
  };

  const fetchChatUsers = useCallback(() => {
    let cancelled = false;
    const fetchData = async () => {
      startLoading();
      if (userInfo?.user_type === HEADQUARTER_USER) {
        try {
          const res = await getG001ForHeadquarterUser(search.facilityId, search.rank, search.name, authState);
          if (!cancelled) {
            setData(res.data);
            res.data.forEach((value) => {
              wsState.socket?.emit("create_chat_room", {
                headquarter_id: userInfo?.id,
                staff_id: value.user_type === STAFF ? value.id : undefined,
                parent_id: value.user_type === PARENT ? value.id : undefined,
              });
            });
          }
        } catch (err) {
          if (!cancelled) {
            checkLoggedIn(err);
            notifyError("データの取得に失敗しました。");
            console.error(err);
          }
        } finally {
          if (!cancelled) {
            stopLoading();
          }
        }
      } else {
        try {
          const res = await getG001ForStaff(search.user_type_partner, search.name, authState);
          if (!cancelled) {
            setData(res.data);
            res.data.forEach((value) => {
              wsState.socket?.emit("create_chat_room", {
                headquarter_id: value.user_type === HEADQUARTER_USER ? value.id : undefined,
                staff_id: userInfo ? userInfo.id : undefined,
                parent_id: value.user_type === PARENT ? value.id : undefined,
              });
            });
          }
        } catch (err) {
          if (!cancelled) {
            checkLoggedIn(err);
            notifyError("データの取得に失敗しました。");
            console.error(err);
          }
        } finally {
          if (!cancelled) {
            stopLoading();
          }
        }
      };
    }
    fetchData();
    return () => {
      cancelled = true;
    }
  }, [search, userInfo, authState, wsState.socket, startLoading, stopLoading, checkLoggedIn]);

  useEffect(() => {
    fetchChatUsersRef.current = fetchChatUsers;
  }, [fetchChatUsers]);

  useEffect(() => {
    fetchChatUsers();
  }, [fetchChatUsers, read])

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchFcFacilities(authState, "");
        if (!cancelled) {
          setFcForSelect(getFcList(FC_LIST_TYPE.ADD_INIT, res.data));
          dispatch(listActions.makeFcList(getFcListForRedux(res.data)));
        }
      } catch (err) {
        if (!cancelled) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };
    if (fcForSelect.length == 0) {
      if (fcListState.select_list.length == 0) {
        fetchData();
      } else {
        setFcForSelect(getFcListFromRedux(FC_LIST_TYPE.ADD_INIT, fcListState.select_list));
      }
    }
    return () => {
      cancelled = true;
    }
  }, [authState, dispatch, checkLoggedIn, fcForSelect.length, fcListState.select_list, startLoading, stopLoading]);

  useEffect(() => {
    if (!wsState.socket) return;

    const refetchWithDelay = () => {
      // DB反映よりWS通知が先行する場合に備え、短い遅延で再取得をリトライ
      const delays = [200, 800];
      delays.forEach((d) => setTimeout(() => fetchChatUsersRef.current?.(), d));
    };

    // 新実装（roomベース）
    wsState.socket.on("receive_chat", refetchWithDelay);
    // 旧実装（login/partnerベース）
    wsState.socket.on("update_chat_room", refetchWithDelay);
    
    return () => {
      wsState.socket?.off("receive_chat", refetchWithDelay);
      wsState.socket?.off("update_chat_room", refetchWithDelay);
    };
  }, [wsState.socket]);

  // 接続が開通したら一覧を再取得し、チャットルームに再参加
  useEffect(() => {
    if (wsState.connected) {
      fetchChatUsersRef.current?.();
    }
  }, [wsState.connected]);

  useEffect(() => {
    state.chat_user_id && pushG002(state.chat_user_id, state.chat_user_type);
  }, [location])

  return (
    <Split
      sizes={[25, 75]}
      gutterSize={20}
      direction="horizontal"
      className="plate d-flex chat-container"
    >
      <div className="chat_rooms_container">
        <LoadingSpinner isLoading={isLoading} />
        {userInfo?.user_type === HEADQUARTER_USER &&
          <>
            <Select
              value={getSelectValue(search.facilityId, fcForSelect)}
              options={fcForSelect}
              className="G001_form w-100"
              onChange={(e: any) => {
                setSearch({ ...search, facilityId: e.value });
              }}
              placeholder="施設"
            />
          </>}
        {userInfo?.user_type === STAFF &&
          <Select
            options={[
              { value: HEADQUARTER_USER, label: "本部スタッフ" },
              { value: PARENT, label: "保護者" },
            ]}
            className="G001_form w-100"
            onChange={(e: any) => {
              setSearch({ ...search, user_type_partner: e.value });
            }}
            placeholder="対象"
          />}
        <TextBox
          class="G001_form"
          placeholder="名前"
          value={search.name}
          onChange={(value: string) => {
            setSearch({ ...search, name: value });
          }}
        />
        <div className="chat_rooms">
          {data?.map((room, i) => (
            <div
              key={i}
              className="chat_rooms_item d-flex on_same_row"
              onClick={() => pushG002(room.id, room.user_type)}
            >
              <Pict url={room.profile_pic_url} size={SIZES.M} />
              <div className="chat_rooms_info d-flex on_same_row">
                <div>
                  <div className="on_same_row">
                    <small className="mx-1">{room.facility_name}</small>
                  </div>
                  <div className="mx-1">{(room.family_name + room.given_name).length > 10 ? (room.family_name + room.given_name).substring(0, 10) + "..." : (room.family_name + room.given_name)}</div>
                </div>
                {room.unread_count !== 0 && (
                  <div className="unread_count">
                    {room.unread_count}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-9">
        {userId && <G002 user_id={userId} user_type={userType} setRead={setRead} />}
      </div>
    </Split>
  );
};

export default G001;
